import moment from 'moment-timezone';
import { FORMATS } from '../core/formats';
import { isValidScheduleDate, isValidScheduleTime } from '../schedule-field-input';
/** Normalize to YYYY-MM-DD (handles ISO strings from storage). */
export function normalizeFeeDateString(value) {
    if (!value)
        return '';
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
    }
    return trimmed;
}
export function normalizeFeeDateSlot(slot) {
    const fromDate = normalizeFeeDateString(slot.fromDate);
    const toDate = normalizeFeeDateString(slot.toDate || slot.fromDate) || fromDate;
    return {
        ...slot,
        fromDate,
        toDate,
    };
}
function isValidStoredFeeDateSlot(slot) {
    if (!slot.fromDate || !isValidScheduleDate(slot.fromDate)) {
        return false;
    }
    const toDate = slot.toDate || slot.fromDate;
    if (!isValidScheduleDate(toDate)) {
        return false;
    }
    if (moment(toDate, FORMATS.moment.date, true).isBefore(moment(slot.fromDate, FORMATS.moment.date, true), 'day')) {
        return false;
    }
    if (slot.h24) {
        return true;
    }
    if (!slot.open || !slot.close) {
        return false;
    }
    if (!isValidScheduleTime(slot.open) || !isValidScheduleTime(slot.close)) {
        return false;
    }
    return true;
}
export function getValidSpecificDates(fee) {
    return (fee.specific_dates || [])
        .map(normalizeFeeDateSlot)
        .filter((slot) => !!slot.fromDate && isValidStoredFeeDateSlot(slot));
}
export function getEffectiveSpecificDates(fee, _special_hours) {
    return getValidSpecificDates(fee);
}
export function isFeeScheduledBySpecificDates(fee, special_hours) {
    return getEffectiveSpecificDates(fee, special_hours).length > 0;
}
export function feeSpecificDateSlotApplies(slot, dt, timezone) {
    const normalized = normalizeFeeDateSlot(slot);
    if (!normalized.fromDate) {
        return false;
    }
    const fromDate = moment.tz(normalized.fromDate, FORMATS.moment.date, timezone).startOf('day');
    const toDate = moment.tz(normalized.toDate, FORMATS.moment.date, timezone).endOf('day');
    if (!fromDate.isValid() || !toDate.isValid()) {
        return false;
    }
    if (!dt.isBetween(fromDate, toDate, 'day', '[]')) {
        return false;
    }
    if (normalized.h24) {
        return true;
    }
    if (normalized.open && normalized.close) {
        const date = dt.locale('en').format(FORMATS.moment.date);
        const open = moment.tz(`${date} ${normalized.open}`, FORMATS.moment.datetime, timezone);
        const close = moment.tz(`${date} ${normalized.close}`, FORMATS.moment.datetime, timezone);
        return dt.isSameOrAfter(open) && dt.isSameOrBefore(close);
    }
    return true;
}
export function feeRecurringHoursApplies(fee, dt, timezone, isTimeWithin) {
    if (!fee.hours || fee.hours.length === 0) {
        return false;
    }
    return isTimeWithin({
        dt,
        hours: fee.hours,
        last_offset: 0,
        first_offset: 0,
        timezone,
    }).isWithin;
}
export function getOrderDueDatetime(args) {
    if (args.due === 'now') {
        return args.momentNow();
    }
    if (args.due !== 'later') {
        return null;
    }
    const date = (args.date || '').trim();
    const time = (args.time || '').trim();
    if (!date || !time) {
        return null;
    }
    const dt = args.momentFromFormat(`${date} ${time}`, FORMATS.moment.datetime);
    return dt.isValid() ? dt : null;
}
/** Whether fee schedule (specific dates and/or recurring hours) matches order due time. */
export function feeScheduleMatches(args) {
    const specificDates = getEffectiveSpecificDates(args.fee, args.special_hours);
    const hasSpecific = specificDates.length > 0;
    const hasRecurring = (args.fee.hours || []).length > 0;
    // No schedule constraints — fee can apply any time (subject to other conditions).
    if (!hasSpecific && !hasRecurring) {
        return true;
    }
    if (!args.dueDatetime || !args.dueDatetime.isValid()) {
        return false;
    }
    const specificMatches = hasSpecific
        ? specificDates.some((slot) => feeSpecificDateSlotApplies(slot, args.dueDatetime, args.timezone))
        : false;
    const recurringMatches = hasRecurring
        ? feeRecurringHoursApplies(args.fee, args.dueDatetime, args.timezone, args.isTimeWithin)
        : false;
    if (hasSpecific && hasRecurring) {
        return specificMatches || recurringMatches;
    }
    if (hasSpecific) {
        return specificMatches;
    }
    return recurringMatches;
}
export function stripFeePromoFromSpecialHourSlot(slot) {
    const cleaned = { ...slot };
    delete cleaned.fee;
    delete cleaned.discount;
    return cleaned;
}
//# sourceMappingURL=fee-schedule.js.map